<template>
  <div class="tasks">
    <div class="task-title">任务消息</div>
    <div class="task-content">
      <template v-if="tasks.length">
        <div v-for="o in tasks" :key="o.id" class="task-tr-wrap">
          <div :class="['task-tr', 'xn-flex-center']">
            <!-- 任务图标 -->
            <div
              :class="['xn-flex-center', { 'xn-pointer': isClickStatus.includes(o.taskStatus) }]"
              @click="previewDetail(o)"
            >
              <xn-icon class="task-icon" :icon="getTaskType(o.taskType, 'icon')" />
            </div>
  
            <div
              :class="['task-td', { 'xn-pointer': isClickStatus.includes(o.taskStatus) }]"
              @click="previewDetail(o)"
            >
              <div class="mb2">
                <!-- 任务名称 -->
                <span class="task-subtitle s4-mr">
                  {{ getTaskType(o.taskType, 'text') }}
                </span>
                <!-- 仅上传任务 -->
                <span class="task-tag" v-if="isTempTask.includes(o.taskType)">
                  临时任务
                </span>
              </div>
              <!-- 任务状态 -->
              <div class="mb2 xn-tip xn-flex-center">
                <span>状态：</span>
                <span class="task-column" :style="`color: ${$util.getBadgeColor(o.taskStatus)}`">
                  {{ getTaskStatus(o.taskStatus) }}
                </span>
              </div>
              <!-- 任务详情、说明 -->
              <div class="task-column">
                <span v-if="['RUNNING_FAIL', 'STOP'].includes(o.taskStatus)" class="xn-error">
                  任务失败，请点击查看详情
                </span>
                <span v-if="['WAIT'].includes(o.taskStatus)">
                  等待中
                </span>
                <!-- 进行中 -->
                <span v-if="['RUNNING'].includes(o.taskStatus)">
                  {{ getRunningDesc(o.taskType, o) }}
                </span>
                <!-- 已完成 -->
                <span v-if="['RUNNING_SUCCESS'].includes(o.taskStatus)">
                  {{ getSuccessDesc(o.taskType, o) }}
                </span>
              </div>
            </div>
  
            <!-- 任务状态 -->
            <div class="task-opera">
              <span
                v-if="['RUNNING', 'WAIT'].includes(o.taskStatus) && ['SITE_MOVE'].includes(o.taskType)"
                class="task-column xn-pointer"
                @click="toStop(o)"
              >
                <CloseOutlined />
              </span>
              <span v-else class="task-column">
                {{ o.endTime ? o.endTime.slice(0, 10) : '' }}
              </span>
            </div>
          </div>
        </div>
      </template>
      <a-empty v-else :image="simpleImage" description="近期无任务" />
    </div>
    <div class="task-more" :style="{ marginTop: tasks.length === 1 ? '60px' : '8px' }">
      <a-button type="link" @click="toTaskCenter">
        查看全部
      </a-button>
    </div>
  </div>

  <a-drawer title="任务详情" :open="open" @close="onClose">
    <detail :row="curRow" />
  </a-drawer>
</template>

<script setup lang="ts">
import { ref, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { Empty } from 'ant-design-vue'
import { CloseOutlined } from '@ant-design/icons-vue'
import { stopTask } from '@/api/task'
import Detail from '@/views/taskCenter/detail.vue'


interface Props {
  tasks: any[]
  getTasks: Function
}

const props = defineProps<Props>()
const store = useStore()
const router = useRouter()
const open = ref<boolean>(false)
const curRow = ref<any>({})
const simpleImage = Empty.PRESENTED_IMAGE_SIMPLE
// 可点击状态
const isClickStatus = ['RUNNING_SUCCESS', 'STOP', 'RUNNING_FAIL']
// 可临时任务
const isTempTask = ['UPLOAD_FILE', 'COMPRESS', 'DECOMPRESS']

const closeTaskBox = () => {
  nextTick(() => {
    store.commit('tempTask/triggerTask', false)
  })
}

const toTaskCenter = () => {
  router.push('/taskCenter')
  closeTaskBox()
}

const onClose = () => open.value = false
const previewDetail = (row) => {
  if (isClickStatus.includes(row.taskStatus)) {
    curRow.value = row
    open.value = true
    closeTaskBox()
  }
}

const extractLastSlash = (str) => {
  let res = ''
  if (str) {
    const lastIndex = str.lastIndexOf('/')
    res = str.substring(lastIndex + 1)
  }
  return res
}

const getRunningDesc = (type, task) => {
  if (type === 'UPLOAD_FILE') {
    return `已上传${task.uploaded}个文件${task.size ? `（${task.size}）` : ''}，${task.failed ? '失败'+ task.failed +'个，' : ''}剩余${task.uploading}个文件，正在上传第${task.whichFile}个文件进度${task.ratio}%`
  }
  if (type === 'SITE_MOVE') {
    let curFile = ''
    if (task.file?.processing) {
      curFile = extractLastSlash(task.file.processing)
    }
    return `已搬运${task.file?.copiedFiles || 0}个文件${task.file?.copiedBytes ? `（${task.file.copiedBytes}）` : ''}，正在传输${curFile}`
  }
  if (type === 'DECOMPRESS') {
    const filename = extractLastSlash(task.filename)
    return `正在解压 ${filename} ${task.processed}/${task.total}`
  }
  if (type === 'COMPRESS') {
    if (task.progress) {
      return `正在下载，进度${task.progress}%`
    }
    if (task.downloaded) {
      return `正在下载，已下载${task.downloaded}，下载速度${task.rate}`
    }
    return '正在下载'
  }
  if (type === 'APP_INSTALL') {
    try {
      return `正在安装 ${JSON.parse(task.taskParam)?.appName}`
    } catch(e) {
      return ''
    }
  }
  if (type === 'WEB_BACKUP') return '正在备份'
  if (type === 'DB_BACKUP') return '正在备份'
  if (type === 'WEB_BACKUP_RESTORE') return '正在恢复'
}

const getSuccessDesc = (type, task) => {
  if (type === 'UPLOAD_FILE') {
    return `共上传${task.succeed}个文件${task.totalSize ? `（${task.totalSize}）` : ''}`
  }
  if (type === 'SITE_MOVE') {
    return `共搬运${task.file?.copiedFiles || 0}个文件${task.file?.copiedBytes ? `（${task.file.copiedBytes}）` : ''}`
  }
  if (type === 'DECOMPRESS') return '解压完成'
  // if (type === 'COMPRESS') return `已压缩下载为 ${extractLastSlash(task.filename)}`
  if (type === 'COMPRESS') return '下载文件任务已完成'
  if (type === 'APP_INSTALL') {
    try {
      const { appName, targetPath } = JSON.parse(task.taskParam)
      return `${appName} 已安装到 ${targetPath} 目录`
    } catch(e) {
      return ''
    }
  }
  if (type === 'WEB_BACKUP') {
    if (task?.taskInfo === 'empty') {
      return '不存在可进行备份的数据'
    }
    try {
      return `已备份到 ${task.taskInfo.slice(task.taskInfo.lastIndexOf('/') + 1)}`
    } catch(e) {
      return ''
    }
  }
  if (type === 'DB_BACKUP') {
    if (task?.taskInfo === 'empty') {
      return '不存在可进行备份的数据'
    }
    try {
      return `已备份到 ${task.taskInfo.slice(task.taskInfo.lastIndexOf('/') + 1)}`
    } catch(e) {
      return ''
    }
  }
  if (type === 'WEB_BACKUP_RESTORE') return '已恢复网站文件'
}

const getTaskStatus = (value) => {
  return Xinnet.settings.taskStatusOpts.find((o) => (
    o.value === value
  ))?.text
}

const getTaskType = (value, key) => {
  return Xinnet.settings.taskTypeOpts.find((o) => {
    return o.value === value
  })?.[key]
}

const stop = async (row, done?) => {
  try {
    const res: any = await stopTask(row.id)
    Xinnet.message.success(res.res)

    const { hostInfo } = store.state
    if (hostInfo?.hostService?.hostName) {
      props.getTasks?.(hostInfo.hostService.hostName, false)
    }
  } catch(e) {
    
  } finally {
    done?.()
  }
}

const openStopModal = (row) => {
  const content = row.taskType === 'SITE_MOVE'
    ? '网站搬家终止后，将无任何文件保存到目标路径，是否要终止已经进行的任务?'
    : '是否要终止已经进行的任务？'
  const api = row.taskType === 'UPLOAD_FILE'
    ? (done) => (store.commit('tempTask/stopTask'), done())
    : (done) => stop(row, done)

  store.commit('modal/open', {
    uid: 'tip',
    modal: {
      title: '确认终止',
      content,
      okDanger: true,
      api,
    }
  })
}

const toStop = (o) => {
  o.taskStatus === 'WAIT' ? stop(o) : openStopModal(o)
}
</script>

<style lang="scss" scoped>
.tasks {
  width: 360px;
  min-height: 241px;
  background: #FFFFFF;
  box-shadow: 0px 9px 28px 8px rgba(0,0,0,0.05),
    0px 6px 16px 0px rgba(0,0,0,0.08),
    0px 3px 6px -4px rgba(0,0,0,0.12);
  .task-title {
    padding: 12px 16px;
    font-weight: 600;
    font-size: 16px;
    color: rgba(0,0,0,0.88);
    line-height: 22px;
    border-bottom: 1px solid #E9E9E9;
  }
  .task-content{
    min-height: 112px;
  }
  .task-tr-wrap {
    padding: 14px 16px;
    &:hover {
      background: rgba(0,0,0,0.03);
    }
  }
  .task-more {
    text-align: center;
    padding-bottom: 10px;
  }
  .task-icon {
    width: 40px;
    height: 40px;
    margin-right: 14px;
  }
  .task-td {
    flex: 1 1 auto;
  }
  .task-subtitle {
    font-weight: 600;
    font-size: 14px;
    color: rgba(0,0,0,0.85);
    line-height: 22px;
  }
  .task-tag {
    display: inline-block;
    width: 64px;
    height: 22px;
    line-height: 20px;
    font-weight: 400;
    font-size: 12px;
    color: #1677FF;
    background: #E6F4FF;
    border-radius: 4px;
    border: 1px solid #BAE0FF;
    text-align: center;
  }
  .task-column {
    font-weight: 400;
    font-size: 12px;
    color: rgba(0,0,0,0.45);
    line-height: 20px;
    word-break: break-all;
  }
  .task-opera {
    flex: 0 0 80px;
    text-align: right;
    // align-self: start;
  }
}
</style>
